import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@comandero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { EmailTemplateEditor } from "./email-template-editor";

type EmailTemplate = components["schemas"]["EmailTemplate"];

interface EmailTemplateManagerProps {
  /**
   * Editor mode, derived from URL (campaign #15b — addressable):
   *   - `"new"`  → `/admin/email/new`        (crear plantilla)
   *   - `"edit"` → `/admin/email/$templateId` (editar; `editId` es el id)
   *   - ausente  → `/admin/email`            (solo lista)
   */
  mode?: () => "new" | "edit" | undefined;
  /** Template id to edit (when `mode() === "edit"`). */
  editId?: () => string | undefined;
  /** Cierra el editor navegando de vuelta a `/admin/email`. */
  onClose?: () => void;
  /** Opens creation editor by navigating to `/admin/email/new`. */
  onNew?: () => void;
  /** Opens edit editor by navigating to `/admin/email/$templateId`. */
  onEdit?: (id: string) => void;
}

/**
 * CRUD for email templates (campaign #11). Lists system and custom templates,
 * and allows creating/editing/deleting them. System templates cannot be deleted.
 * Creation/edit editor is URL-addressable (#15b): `/admin/email/new`
 * y `/admin/email/$templateId`.
 */
export function EmailTemplateManager(props: EmailTemplateManagerProps = {}) {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  /** Mirror of `Capability::ManageContent` (manage_content_middleware). */
  const canWrite = () => can("content.write");

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  // Editor state derived from URL (not local signals): copy/paste
  // URL reopens the same editor on another device.
  const isCreating = createMemo(() => props.mode?.() === "new");
  const isEditing = createMemo(() => props.mode?.() === "edit");
  const editing = createMemo<EmailTemplate | null>(() => {
    if (!isEditing()) {
      return null;
    }
    const id = props.editId?.();
    return templatesQuery.data?.find((tpl) => tpl.id === id) ?? null;
  });
  // Template to edit does not exist (invalid id or deleted) once list loaded.
  const editNotFound = createMemo(
    () => isEditing() && !templatesQuery.isLoading && editing() == null,
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    await templatesQuery.refetch();
    props.onClose?.();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        {/* Crear no informa de nada: se oculta. La LECTURA (listado, cuerpo de
            la plantilla, versiones) sigue intacta para `read_only`. */}
        <Show when={canWrite()}>
          <Button class="gap-2" onClick={() => props.onNew?.()} disabled={isCreating()}>
            <Plus class="h-4 w-4" />
            {t(msg`New Template`)}
          </Button>
        </Show>
      </div>

      <Show when={editNotFound()}>
        <NotFoundState
          title={t(msg`Template not found`)}
          description={t(msg`This email template may have been deleted, or never existed.`)}
          primaryAction={{
            label: t(msg`Back to templates`),
            onClick: () => props.onClose?.(),
          }}
        />
      </Show>

      <Show when={isCreating() || editing()}>
        <EmailTemplateEditor
          template={editing()}
          onSaved={onSaved}
          onCancel={() => props.onClose?.()}
        />
      </Show>

      {/* P23/I3: «no hay respuesta» != «no hay nada». El bloque de
          contenido se ENVUELVE (no se encadena): en error `isLoading` es
          `false`, asi que un `when={!q.isLoading && !q.isError}` con
          `fallback={esqueleto}` pintaria error Y esqueleto a la vez. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1"
                      onClick={() => props.onEdit?.(tpl.id)}
                    >
                      <Pencil class="h-4 w-4" />
                      {t(msg`Edit`)}
                    </Button>
                    <Show when={!tpl.isSystem && canWrite()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
